import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  useDisclosure,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  Text,
  Button,
  SimpleGrid,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Heading,
  Spinner,
  useToast,
} from "@chakra-ui/react";
import { Link, Outlet, useLocation } from "react-router-dom";
import SharedSidebar from "../shared/SharedSidebar";
import Header from "../shared/Header";
import axiosInstance from "../../config/axiosInstance";

const SIDEBAR_WIDTH = "250px";

const DashboardReceptionistCombined = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const location = useLocation();
  const toast = useToast();
  const isDashboardRoute = location.pathname === "/dashboard";

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalOrders: 0,
    delivered: 0,
    pending: 0,
    assigned: 0,
  });

  const fetchDashboardStats = useCallback(async () => {
    setLoading(true);
    try {
      const statsRes = await axiosInstance.get("/api/orders/stats/receptionist");
      setStats({
        totalOrders: statsRes.data?.totalOrders || 0,
        delivered: statsRes.data?.totalDelivered || 0,
        pending: statsRes.data?.totalPending || 0,
        assigned: statsRes.data?.totalAssigned || 0,
      });

      const ordersRes = await axiosInstance.get("/api/orders/receptionist");
      setOrders(ordersRes.data?.orders || []);
    } catch (err) {
      toast({
        title: "Failed to load dashboard data",
        description: err.response?.data?.message || "Server error",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    if (isDashboardRoute) {
      fetchDashboardStats();
    }
  }, [isDashboardRoute, fetchDashboardStats]);

  const currentDate = new Date().toLocaleDateString("en-IN", {
    weekday: "long",
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  return (
    <Box minH="100vh">
      <Box
        display={{ base: "none", md: "block" }}
        position="fixed"
        top={0}
        left={0}
        w={SIDEBAR_WIDTH}
        h="100vh"
        zIndex={1000}
      >
        <SharedSidebar variant="desktop" role="receptionist" />
      </Box>

      <Drawer isOpen={isOpen} placement="left" onClose={onClose} size="xs">
        <DrawerOverlay />
        <DrawerContent p={0}>
          <Box w="250px" h="100vh" overflowY="auto" bg="white">
            <SharedSidebar
              onNavigate={onClose}
              variant="mobile"
              role="receptionist"
            />
          </Box>
        </DrawerContent>
      </Drawer>

      <Box ml={{ base: 0, md: SIDEBAR_WIDTH }}>
        <Header onSidebarOpen={onOpen} />
        <Box p={4} bg="gray.50" minH="calc(100vh - 64px)">
          {isDashboardRoute ? (
            <>
              <Text fontWeight="bold" fontSize="lg">
                Welcome Back!
              </Text>
              <Text fontSize="sm" color="gray.600" mb={6}>
                {currentDate}
              </Text>

              <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} spacing={4} my={6}>
                <Box p={4} bg="white" borderRadius="md" shadow="sm">
                  <Text fontSize="sm" color="gray.500">
                    Total Orders
                  </Text>
                  <Text fontSize="2xl" fontWeight="bold">
                    {stats.totalOrders}
                  </Text>
                </Box>
                <Box p={4} bg="white" borderRadius="md" shadow="sm">
                  <Text fontSize="sm" color="gray.500">
                    Delivered
                  </Text>
                  <Text fontSize="2xl" fontWeight="bold" color="green.600">
                    {stats.delivered}
                  </Text>
                </Box>
                <Box p={4} bg="white" borderRadius="md" shadow="sm">
                  <Text fontSize="sm" color="gray.500">
                    Pending
                  </Text>
                  <Text fontSize="2xl" fontWeight="bold" color="orange.500">
                    {stats.pending}
                  </Text>
                </Box>
                <Box p={4} bg="white" borderRadius="md" shadow="sm">
                  <Text fontSize="sm" color="gray.500">
                    Total Assigned
                  </Text>
                  <Text fontSize="2xl" fontWeight="bold" color="blue.500">
                    {stats.assigned}
                  </Text>
                </Box>
              </SimpleGrid>

              <Box mt={10}>
                <Text fontWeight="semibold">Quick Actions</Text>
                <Text fontSize="sm" color="gray.500" mb={4}>
                  What would you like to do next?
                </Text>

                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <Link to="new-customer">
                    <Box
                      border="2px solid"
                      borderColor="green.500"
                      borderRadius="md"
                      p={6}
                      textAlign="center"
                    >
                      <Button colorScheme="green" variant="ghost">
                        Add Customer
                      </Button>
                    </Box>
                  </Link>
                  <Link to="order-history">
                    <Box
                      border="2px dashed"
                      borderColor="gray.300"
                      borderRadius="md"
                      p={6}
                      textAlign="center"
                    >
                      <Button variant="ghost">Order History</Button>
                    </Box>
                  </Link>
                </SimpleGrid>
              </Box>

              <Box mt={10} bg="white" p={6} borderRadius="md" overflowX="auto">
                <Heading size="md" mb={4}>
                  All Orders Summary
                </Heading>

                {loading ? (
                  <Spinner size="lg" />
                ) : orders.length === 0 ? (
                  <Text>No orders found.</Text>
                ) : (
                  <Table variant="simple">
                    <Thead>
                      <Tr>
                        <Th>S.No</Th>
                        <Th>Bill No</Th>
                        <Th>Amount</Th>
                        <Th>Order Items</Th>
                        <Th>Delivery Dates</Th>
                        <Th>Order Date</Th>
                        <Th>Customer</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {(() => {
                        let serial = 1;

                        return orders.map((order) => {
                          const amount = `₹${order?.payment?.paid || 0}`;
                          const orderDate = new Date(order?.orderDate).toLocaleDateString("en-IN");
                          const name = order?.customer?.name || "-";

                          const items = order.orderDetails
                            .map((d) => d.item)
                            .join(", ");

                          const deliveryDates = order.orderDetails
                            .map((d) =>
                              d.deliveryDate
                                ? new Date(d.deliveryDate).toLocaleDateString("en-IN")
                                : "-"
                            )
                            .join(", ");

                          return (
                            <Tr key={order._id}>
                              <Td>{serial++}</Td>
                              <Td>{order.billNumber}</Td>
                              <Td>{amount}</Td>
                              <Td>{items}</Td>
                              <Td>{deliveryDates}</Td>
                              <Td>{orderDate}</Td>
                              <Td>{name}</Td>
                            </Tr>
                          );
                        });
                      })()}
                    </Tbody>
                  </Table>
                )}
              </Box>
            </>
          ) : (
            <Outlet />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default DashboardReceptionistCombined;
